/**
 * Audio device utilities
 *
 * Provides helpers for audio device detection, selection, and Bluetooth handling.
 */

import { logger } from './logger';
import { isMac, isWindows } from './platform';
import {
  STORAGE_KEYS as GLOBAL_STORAGE_KEYS,
  getItem as storageGet,
  setItem as storageSet,
} from './storage';

// Re-export storage keys for backward compatibility with existing consumers
export const STORAGE_KEYS = {
  MICROPHONE: GLOBAL_STORAGE_KEYS.SELECTED_MICROPHONE,
  LAST_GOOD_MICROPHONE: GLOBAL_STORAGE_KEYS.LAST_GOOD_MICROPHONE,
  SPEAKER: GLOBAL_STORAGE_KEYS.SELECTED_SPEAKER,
  MAC_BT_TIP_DISMISSED: GLOBAL_STORAGE_KEYS.BLUETOOTH_TIP_DISMISSED,
} as const;

// Known-problem Bluetooth patterns (avoid using as mic on macOS)
const PROBLEM_BT_REGEX =
  /sony|wh-\d{3,4}|wf-\d{3,4}|hands[- ]?free|headset|ag audio/i;

// Known-good Apple devices
const APPLE_BT_REGEX = /airpods(\s+pro|\s+max)?|beats/i;

// Built-in device patterns
const BUILT_IN_REGEX = /built[- ]?in|macbook/i;

/**
 * Check if a device label indicates a problematic Bluetooth device
 */
export function isProblemBluetoothDevice(label: string): boolean {
  return PROBLEM_BT_REGEX.test(label) && !APPLE_BT_REGEX.test(label);
}

/**
 * Check if a device label indicates a built-in device
 */
export function isBuiltInDevice(label: string): boolean {
  return BUILT_IN_REGEX.test(label);
}

/**
 * Check if a device label indicates an Apple Bluetooth device
 */
export function isAppleBluetoothDevice(label: string): boolean {
  return APPLE_BT_REGEX.test(label);
}

/**
 * Filter microphones from device list
 */
export function filterMicrophones(
  devices: MediaDeviceInfo[],
): MediaDeviceInfo[] {
  return devices.filter(
    d => d.kind === 'audioinput' && d.deviceId !== 'communications',
  );
}

/**
 * Filter speakers from device list
 */
export function filterSpeakers(devices: MediaDeviceInfo[]): MediaDeviceInfo[] {
  return devices.filter(
    d => d.kind === 'audiooutput' && d.deviceId !== 'communications',
  );
}

/**
 * Select the best default microphone based on platform
 *
 * - Windows: Avoids the generic "default" device in favor of the actual device
 * - macOS: Avoids problematic Bluetooth devices in favor of built-in mic
 * - Other: Uses system default
 *
 * @returns The selected microphone, or undefined if no microphones are available
 */
export function selectDefaultMicrophone(
  mics: MediaDeviceInfo[],
  hasPermission: boolean,
  currentSelection?: string,
): MediaDeviceInfo | undefined {
  // If user has already selected a non-default device, keep it
  const existingSelection = mics.find(m => m.deviceId === currentSelection);
  if (existingSelection && existingSelection.deviceId !== 'default') {
    return existingSelection;
  }

  if (isWindows()) {
    return selectDefaultMicrophoneWindows(mics);
  }

  return selectDefaultMicrophoneMac(mics, hasPermission);
}

/**
 * Windows-specific microphone selection
 * Tries to find the actual device behind "default" to avoid Windows quirks
 */
function selectDefaultMicrophoneWindows(
  mics: MediaDeviceInfo[],
): MediaDeviceInfo {
  logger.debug('Windows detected, using smart default selection');

  // Try to find the actual device behind "default"
  const defaultDevice = mics.find(m => m.deviceId === 'default');
  let selectedDevice: MediaDeviceInfo | undefined;

  if (defaultDevice?.label) {
    selectedDevice = mics.find(
      m =>
        m.deviceId !== 'default' &&
        m.label &&
        (m.label === defaultDevice.label ||
          (defaultDevice.groupId && m.groupId === defaultDevice.groupId)),
    );

    if (selectedDevice) {
      logger.debug('Found matching device for default', {
        defaultLabel: defaultDevice.label,
        actualDevice: selectedDevice.label,
        deviceId: selectedDevice.deviceId,
      });
    }
  }

  if (!selectedDevice) {
    selectedDevice = mics.find(m => m.deviceId !== 'default') || mics[0];
    logger.debug('Using first non-default device', {
      device: selectedDevice?.label,
      deviceId: selectedDevice?.deviceId,
    });
  }

  return selectedDevice || mics[0];
}

/**
 * macOS-specific microphone selection
 * Avoids problematic Bluetooth mics in favor of built-in mic
 */
function selectDefaultMicrophoneMac(
  mics: MediaDeviceInfo[],
  hasPermission: boolean,
): MediaDeviceInfo {
  const defaultMic = mics.find(m => m.deviceId === 'default') || mics[0];
  let chosenMic = defaultMic;

  if (isMac() && hasPermission) {
    const label = defaultMic.label || '';
    const defaultIsProblem = isProblemBluetoothDevice(label);

    if (defaultIsProblem) {
      const builtIn = mics.find(m => m.label && isBuiltInDevice(m.label));
      if (builtIn) {
        logger.debug('macOS: Avoiding problem BT mic; selecting built-in mic', {
          default_label: defaultMic.label,
          chosen_label: builtIn.label,
        });
        chosenMic = builtIn;
      }
    }
  }

  return chosenMic;
}

/**
 * Find the default speaker device from device list
 */
export function findDefaultSpeaker(
  speakers: MediaDeviceInfo[],
): MediaDeviceInfo | undefined {
  return (
    speakers.find(
      s =>
        s.deviceId === 'default' ||
        s.label?.toLowerCase().includes('default') ||
        s.label?.toLowerCase().includes('system'),
    ) || speakers[0]
  );
}

/**
 * Get speaker label with fallback messages
 */
export function getSpeakerLabel(
  speaker: MediaDeviceInfo | undefined,
  hasPermission: boolean,
): string {
  if (!speaker) {
    return 'No audio output detected';
  }

  if (speaker.label) {
    return speaker.label;
  }

  if (hasPermission) {
    return 'Default Audio Output';
  }

  return 'Default Audio Output (grant microphone access to see device name)';
}

/**
 * Safely get item from localStorage
 * Delegates to centralized storage utility
 */
export function getStoredDeviceId(key: string): string | null {
  return storageGet(key);
}

/**
 * Safely set item in localStorage
 * Delegates to centralized storage utility
 */
export function setStoredDeviceId(key: string, value: string): void {
  storageSet(key, value);
}
